"use client";

import { VenuePageCarouselMode } from "@punchup/data/models";
import { useEffect, useState } from "react";
import {
  useUpdateVenuePageCarouselMode,
  useVenuePageCarousel,
} from "../../../hooks/venuePages/carousel";

import { VenuePagesRow } from "@punchup/data";
import { cn } from "@punchup/utils/cn";
import { PunchupRadioCards } from "@punchup/ui-common/punchup/primitives/forms/fields/PunchupRadioCardGroup";
import VenueBannerCarouselSettingsDefault from "./VenueBannerCarouselSettingsDefault";
import VenueBannerCarouselSettingsCustom from "./VenueBannerCarouselSettingsCustom";

const AUTO_ROTATE_DELAY_OPTIONS = [
  { label: "3 seconds", value: 3000 },
  { label: "5 seconds", value: 5000 },
  { label: "7 seconds", value: 7000 },
  { label: "10 seconds", value: 10000 },
] as const;

// Typed against the enum so a typo'd `value` fails typecheck (and the `as VenuePageCarouselMode` cast at
// onChange is then provably sound — PunchupRadioCards is generically string-keyed, so the narrowing belongs
// here at the consumer boundary, not in the primitive).
const CAROUSEL_MODE_OPTIONS: {
  value: VenuePageCarouselMode;
  label: string;
  description: string;
}[] = [
  {
    value: "default",
    label: "Next 5 upcoming shows",
    description: "Automatically display your next 5 shows",
  },
  {
    value: "custom",
    label: "Custom carousel",
    description: "Upload images or select shows",
  },
];

interface Props {
  venuePage: VenuePagesRow;
}

export default function VenueBannerCarouselSettings({ venuePage }: Props) {
  const { data: carouselData } = useVenuePageCarousel(venuePage.id);
  const { mutate: updateCarouselMode, isPending: isUpdatingCarouselMode } =
    useUpdateVenuePageCarouselMode();

  // Local state for optimistic UI - synced with server data
  const [selectedMode, setSelectedMode] = useState<VenuePageCarouselMode>(
    venuePage.carousel_mode
  );

  const [autoRotateDelay, setAutoRotateDelay] = useState<number | null>(
    venuePage.carousel_auto_rotate_delay
  );

  // Sync local state with server data when it loads
  useEffect(() => {
    if (carouselData?.mode) {
      setSelectedMode(carouselData.mode);
    }
    if (carouselData?.autoRotateDelay !== undefined) {
      setAutoRotateDelay(carouselData.autoRotateDelay);
    }
  }, [carouselData?.mode, carouselData?.autoRotateDelay]);

  const handleModeChange = (newMode: VenuePageCarouselMode) => {
    if (newMode === selectedMode) return;

    setSelectedMode(newMode);

    updateCarouselMode({
      venuePageId: venuePage.id,
      mode: newMode,
    });
  };

  const handleAutoRotateToggle = () => {
    const newDelay = autoRotateDelay ? null : 5000;
    setAutoRotateDelay(newDelay);

    updateCarouselMode({
      venuePageId: venuePage.id,
      mode: selectedMode,
      autoRotateDelay: newDelay,
    });
  };

  const handleAutoRotateDelayChange = (value: number) => {
    setAutoRotateDelay(value);

    updateCarouselMode({
      venuePageId: venuePage.id,
      mode: selectedMode,
      autoRotateDelay: value,
    });
  };

  return (
    <div className="flex flex-col gap-y-6 md:gap-y-8">
      {/* Carousel Type Selection */}
      <div className="flex flex-col gap-y-3 md:gap-y-4">
        <h3 className="font-medium text-base text-primary-blue">
          Carousel type
        </h3>
        <PunchupRadioCards
          value={selectedMode}
          onChange={(mode) => handleModeChange(mode as VenuePageCarouselMode)}
          disabled={isUpdatingCarouselMode}
          options={CAROUSEL_MODE_OPTIONS}
        />
      </div>

      {/* Auto-Rotate Settings */}
      <div className="flex flex-col gap-y-3 md:gap-y-4">
        <h3 className="font-medium text-base text-primary-blue">Auto-rotate</h3>
        <div className="flex flex-col gap-y-3">
          <label className="flex items-center gap-x-3 cursor-pointer">
            <button
              type="button"
              role="switch"
              aria-checked={!!autoRotateDelay}
              disabled={isUpdatingCarouselMode}
              onClick={handleAutoRotateToggle}
              className={cn(
                "relative inline-flex h-6 w-11 shrink-0 rounded-full border-2 border-transparent transition-colors",
                autoRotateDelay ? "bg-primary" : "bg-gray-200",
                isUpdatingCarouselMode && "opacity-50"
              )}
            >
              <span
                className={cn(
                  "pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow-sm transition-transform",
                  autoRotateDelay ? "translate-x-5" : "translate-x-0"
                )}
              />
            </button>
            <span className="text-sm text-primary-blue">
              Automatically rotate carousel slides
            </span>
          </label>

          {autoRotateDelay && (
            <div className="flex items-center gap-x-3 pl-14">
              <label
                htmlFor="auto-rotate-delay"
                className="text-sm text-gray-500"
              >
                Delay between slides
              </label>
              <select
                id="auto-rotate-delay"
                value={autoRotateDelay}
                onChange={(e) =>
                  handleAutoRotateDelayChange(Number(e.target.value))
                }
                disabled={isUpdatingCarouselMode}
                className="rounded-md border border-gray-200 px-3 py-1.5 text-sm text-primary-blue"
              >
                {AUTO_ROTATE_DELAY_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      </div>

      {/* Shows Preview (when default mode) */}
      {selectedMode === "default" && (
        <VenueBannerCarouselSettingsDefault venuePage={venuePage} />
      )}

      {/* Custom items list (when custom mode) */}
      {selectedMode === "custom" && (
        <VenueBannerCarouselSettingsCustom
          venuePageId={venuePage.id}
          venueSlug={venuePage.slug}
        />
      )}
    </div>
  );
}
